import _get from 'lodash/get';
import _memoize from 'lodash/memoize';
import sha1 from 'crypto-js/sha1';
import { fido, imageService } from '@bedrock/fido-sdk';
import { getLockPicto, getCSAPicto, getServicePicto } from '@bedrock-rdk/utils';
import {
  DEFAULT_IMAGE_FORMAT,
  FULL_HD,
  IMAGE_ITEMS_DEFAULT_OPTIONS,
  RENDERING_CONTEXT,
  IMAGE_QUALITY,
} from '@bedrock-rdk/constants';

/**
 * @typedef {Object} ImageOptions
 * @property {number} width image width
 * @property {number} height image height
 * @property {('jpeg'|'png'|'webp')} format image format, use "png" for transparency support
 * @property {number} quality image quality, from 0 to 100
 */

export const getImageQueryString = options =>
  Object.entries(options)
    // eslint-disable-next-line no-unused-vars
    .filter(([_, value]) => Boolean(value) || Number.isFinite(value))
    .sort(([key1], [key2]) => (key1 <= key2 ? -1 : 1))
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');

/**
 * Creates a set of options that would be used to query image API basing on defaults merged with template-defined properties
 *
 * @param {ImageOptions?} options image options for a specific content template
 * @returns {Object}
 */
export const constructImageOptions = (options = undefined) =>
  options
    ? {
        ...IMAGE_ITEMS_DEFAULT_OPTIONS,
        image: {
          ...IMAGE_ITEMS_DEFAULT_OPTIONS.image,
          ...options.PRIMARY,
        },
        secondaryImage: {
          ...IMAGE_ITEMS_DEFAULT_OPTIONS.image,
          ...options.SECONDARY,
        },
        logo: {
          ...IMAGE_ITEMS_DEFAULT_OPTIONS.logo,
          ...options.LOGO,
        },
      }
    : IMAGE_ITEMS_DEFAULT_OPTIONS;

export const imageOptionsToQueries = options =>
  Object.entries(options).reduce((result, [key, value]) => {
    result[key] = getImageQueryString(value);

    return result;
  }, {});

/**
 * Depending on the type of the block Layout is attempting to render, we want images to have different sizes. This function
 * maps the blockType to an ImageOption object that Fido's `getImageUrl` function can understand.
 * @param {string} contentTemplateId identifier of a template that will be used to render an item in block
 * @param {Object} templatesOptions a set of template-defined options, image options are keyed by rendering context
 * @param {('FULL_HD'|'HD')} screenSize Either HD, FULL_HD or ULTRA_HD
 * @param {string} context one of RENDERING_CONTEXT values, falls back to the default context when not defined
 * @returns {Object} image configuration to be passed to Fido
 */
export const getContentImageOptions = _memoize(
  (contentTemplateId, templatesOptions, screenSize, context = RENDERING_CONTEXT.GENERAL) =>
    imageOptionsToQueries(
      constructImageOptions(
        _get(templatesOptions, `${contentTemplateId}.imageOptions.${context}.${screenSize}`) ||
          _get(templatesOptions, `${contentTemplateId}.imageOptions.${RENDERING_CONTEXT.GENERAL}.${screenSize}`),
      ),
    ),
  (id, _, screenSize, context = RENDERING_CONTEXT.GENERAL) => `${id}_${screenSize}_${context}`,
);

/**
 * Returns default set of options for screensaver images
 *
 * @param {('FULL_HD'|'HD')} screenSize
 * @returns {Object}
 */
export const getScreenSaverImageOptions = _memoize(screenSize =>
  imageOptionsToQueries(
    constructImageOptions({
      PRIMARY: {
        width: screenSize === FULL_HD ? 1920 : 1280,
        height: screenSize === FULL_HD ? 1080 : 720,
        format: DEFAULT_IMAGE_FORMAT,
        quality: IMAGE_QUALITY,
      },
    }),
  ),
);

export const getImageUrl = (id, query) => {
  const rawUrl = imageService.getRouteUrl(`${id}/raw`);
  const path = imageService.getRoutePath(`${id}/raw?${query}`, true);
  const hash = sha1(`${path}${fido.get('imageSalt')}`);

  if (hash) {
    return `${rawUrl}?${query}&hash=${hash}`;
  }

  return rawUrl;
};

/**
 * Adds a `src` key to layout images
 *
 * @param {Object} image a layout image object
 * @param {string} image.id a layout image id
 * @param {ImageOptions} options
 * @returns {Object}
 */
const getItemImage = (image, options) => {
  if (image && options) {
    image.src = getImageUrl(image.id, options);
  }

  return image;
};

export const prepareItemImages = (item, imageOptions, forceGhostServicePicto = false) => {
  // Images
  item.image = getItemImage(item.image, imageOptions.image);
  item.secondaryImage = getItemImage(item.secondaryImage, imageOptions.secondaryImage);
  item.logo = getItemImage(item.logo, imageOptions.logo);

  // Icons
  item.csaPicto = getCSAPicto(item.pictos);
  item.servicePicto = getServicePicto(item.pictos);
  item.lockPicto = getLockPicto(item.pictos);

  if (forceGhostServicePicto && item.servicePicto && item.servicePicto.name) {
    item.servicePicto.name = item.servicePicto.name.endsWith('_ghost')
      ? item.servicePicto.name
      : item.servicePicto.name.replace(/(_dark$|$)/, '_ghost');
  }

  return item;
};
